/**
 * Route Permission Utilities
 * Handles route-based permission checking and validation
 */

// Define route to permission mapping using patterns instead of exact names
export const ROUTE_PERMISSION_MAP = {
  "/dashboard": {
    pattern: "dashboard",
    relatedRoutes: [],
  },
  "/property": {
    pattern: "property",
    relatedRoutes: [],
  },
  "/manage-bookings": {
    pattern: "manage bookings",
    relatedRoutes: [],
  },
  "/booking-requests": {
    pattern: "booking requests",
    relatedRoutes: ["/booking-requests/booking-detail"],
  },
  "/task-execution": {
    pattern: "task execution",
    relatedRoutes: [
      "/task-execution/task-basic-detail",
      "/task-execution/task-adhoc-detail",
      "/task-execution/task-markAsComplete",
    ],
  },
  "/chat": {
    pattern: "chat",
    relatedRoutes: [],
  },
  "/staff-management": {
    pattern: "staff management",
    relatedRoutes: [],
  },
  "/job-archive": {
    pattern: "job archive",
    relatedRoutes: ["/job-archive/job-archive-detail"],
  },
  "/setting": {
    pattern: "setting",
    relatedRoutes: [],
  },
  "/help-support": {
    pattern: "help & support",
    relatedRoutes: [],
  },
  "/privacy-policy": {
    pattern: "privacy policy",
    relatedRoutes: [],
  },
  "/terms-and-conditions": {
    pattern: "terms and conditions",
    relatedRoutes: [],
  },
  "/working-hours": {
    pattern: "working hours",
    relatedRoutes: [],
  },
};

export const PUBLIC_AUTHENTICATED_ROUTES = ["/401", "/404", "/", "/dashboard"];

/**
 * Check if user has access to the given route based on permissions
 * @param {string} path - Current route path
 * @param {Array} permissions - User permissions array
 * @returns {boolean} - Whether user has access
 */
export const checkRouteAccess = (path, permissions) => {
  // Normalize all permission strings to lowercase for comparison
  const normalizedPermissions = permissions.map((p) => p.toLowerCase());

  // First, check for exact route matches
  const basePath = Object.keys(ROUTE_PERMISSION_MAP).find(
    (route) => path === route || path.startsWith(route + "/")
  );

  if (basePath) {
    const routeConfig = ROUTE_PERMISSION_MAP[basePath];
    const requiredPattern = routeConfig.pattern.toLowerCase();

    // Check if the required pattern exists in the permissions
    const hasPermission = normalizedPermissions.some((permission) =>
      permission.includes(requiredPattern)
    );

    if (hasPermission) {
      return true;
    }
  }

  // If no exact match found, check for dynamic match using path tokens
  const pathForMatching = path
    .split("/")
    .filter(Boolean)
    .join(" ")
    .replace(/-/g, " ")
    .toLowerCase();

  const hasDynamicPermission = normalizedPermissions.some((permission) =>
    permission.includes(pathForMatching) || pathForMatching.includes(permission)
  );

  return hasDynamicPermission;
};


/**
 * Get accessible routes for a given set of permissions
 * @param {Array} permissions - User permissions array
 * @param {string} userType - Type of the user
 * @returns {array} - Array of accessible routes
 */
export const getAccessibleRoutes = (permissions, userType) => {
  // If user is admin, return all routes
  if (userType === "dealer") {
    const allRoutes = [
      ...PUBLIC_AUTHENTICATED_ROUTES,
      ...Object.keys(ROUTE_PERMISSION_MAP),
    ];

    // Add all related routes
    Object.values(ROUTE_PERMISSION_MAP).forEach((config) => {
      allRoutes.push(...config.relatedRoutes);
    });

    return allRoutes;
  }

  if (!permissions || !Array.isArray(permissions)) {
    return PUBLIC_AUTHENTICATED_ROUTES;
  }

  const accessibleRoutes = [...PUBLIC_AUTHENTICATED_ROUTES];

  // Add routes based on permission patterns
  Object.entries(ROUTE_PERMISSION_MAP).forEach(([route, config]) => {
    const requiredPattern = config.pattern.toLowerCase();

    const matchedPermission = permissions.find((permission) => {
      const permissionName = permission.permission_name?.toLowerCase();
      return permissionName && permissionName.includes(requiredPattern);
    });

    if (matchedPermission?.read === true) {
      accessibleRoutes.push(route);
      // Add related routes
      accessibleRoutes.push(...config.relatedRoutes);
    }
  });

  return accessibleRoutes;
};

/**
 * Check if a route is public (doesn't require specific permissions)
 * @param {string} path - Route path to check
 * @returns {boolean} - Whether the route is public
 */
export const isPublicRoute = (path) => {
  return PUBLIC_AUTHENTICATED_ROUTES.some(
    (route) => path === route || path.startsWith(route + "/")
  );
};

/**
 * Get permission for a specific route
 * @param {string} path - Route path
 * @param {Array} permissions - User permissions array
 * @returns {Object|null} - Permission object or null if not found
 */
export const getRoutePermission = (path, permissions) => {
  const basePath = Object.keys(ROUTE_PERMISSION_MAP).find(
    (route) => path === route || path.startsWith(route + "/")
  );

  if (basePath) {
    const routeConfig = ROUTE_PERMISSION_MAP[basePath];
    const requiredPattern = routeConfig.pattern.toLowerCase();

    return permissions.find((permission) => {
      const permissionName = permission.permission_name?.toLowerCase();
      return permissionName && permissionName.includes(requiredPattern);
    });
  }

  return null;
};